import { Box, Icon, SxProps } from "@outschool/backpack";
import { IconDefinition, faChevronDown } from "@outschool/icons";
import {
  Modal,
  Overlay,
  ToggleButton,
} from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import React from "react";
import { Manager, Popper, Reference } from "react-popper";

type FilterDropdownProps = {
  open: boolean;
  filterName?: string;
  filterValue?: string;
  onClose(): void;
  onOpen(): void;
  buttonSx?: SxProps;
  filterIcon?: IconDefinition;
  isActiveWhenOpen?: boolean;
  showChevron?: boolean;
  referenceElement?: any;
};

export default function FilterDropdown(
  props: React.PropsWithChildren<FilterDropdownProps>
) {
  const {
    open,
    filterName,
    filterValue,
    children,
    referenceElement,
    onClose,
    filterIcon,
    isActiveWhenOpen = true,
    buttonSx,
    showChevron = true,
  } = props;

  const onClick = () => {
    const { open, onClose, onOpen } = props;
    if (open) {
      onClose();
    } else {
      onOpen();
    }
  };

  const active = (open && isActiveWhenOpen) || !!filterValue;

  return (
    <Manager>
      <div>
        <Reference>
          {({ ref }) => (
            <ToggleButton
              size="small"
              enabled={active}
              onClick={onClick}
              ref={ref}
              sx={buttonSx}
            >
              {filterIcon && (
                <Icon
                  icon={filterIcon}
                  sx={{
                    marginRight: filterValue ? "0.5em" : undefined,
                  }}
                />
              )}
              {filterValue || filterName}
              {showChevron && (
                <Icon
                  icon={faChevronDown}
                  sx={{
                    marginLeft: "0.5em",
                  }}
                />
              )}
            </ToggleButton>
          )}
        </Reference>

        {open && (
          <FilterDropdownOverlay
            referenceElement={referenceElement}
            onClose={onClose}
          >
            {children}
          </FilterDropdownOverlay>
        )}
      </div>
    </Manager>
  );
}

type FilterDropdownOverlayProps = {
  onClose(): void;
  referenceElement: any;
};

function FilterDropdownOverlay({
  children,
  referenceElement,
  onClose = () => {},
}: React.PropsWithChildren<FilterDropdownOverlayProps>) {
  const popperProps = referenceElement ? { referenceElement } : {};
  const isMobile = Screen.useIsMobile();

  return (
    <Popper
      placement="bottom-start"
      positionFixed
      modifiers={{
        preventOverflow: {
          padding: 0,
          boundariesElement: "viewport",
        },
        flip: {
          enabled: false,
        },
        scroll: {
          enabled: true,
          fn(data) {
            data.styles.overflowY = "auto";
            return data;
          },
        },
      }}
      {...popperProps}
    >
      {({ ref, style }) => {
        return isMobile ? (
          <Modal
            open
            fullBleed={true}
            onClose={onClose}
            closeViaMaskClick={true}
            closeViaEscButton={true}
          >
            <Box
              sx={{
                paddingBottom: "100px",
              }}
            >
              {children}
            </Box>
          </Modal>
        ) : (
          <React.Fragment>
            <Overlay
              ref={ref}
              sx={{
                width: isMobile ? "100%" : undefined,
                marginTop: !isMobile ? "0.5em" : undefined,
                padding: "0.75em 1em 1em 1em",
              }}
              style={style}
            >
              {children}
            </Overlay>
          </React.Fragment>
        );
      }}
    </Popper>
  );
}
